import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { statsAPI } from '../services/api';

const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(n);

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const StatCard: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const today = new Date();
  const from = ymd(new Date(today.getTime() - 6 * 86400000));
  const to = ymd(today);
  const year = String(today.getFullYear());

  const { data: summary } = useQuery({
    queryKey: ['stats-summary'],
    queryFn: async () => (await statsAPI.summary()).data,
  });

  const { data: daily } = useQuery({
    queryKey: ['stats-daily', from, to],
    queryFn: async () => (await statsAPI.daily(from, to)).data || [],
  });

  const { data: monthly } = useQuery({
    queryKey: ['stats-monthly', year],
    queryFn: async () => (await statsAPI.monthly(year)).data || [],
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>대시보드</Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard label="오늘 발행 포인트" value={fmt(summary?.pointsIssued ?? 0)} color="#2e7d32" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard label="오늘 사용 포인트" value={fmt(summary?.pointsUsed ?? 0)} color="#c62828" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard label="DAU (활성 유저)" value={fmt(summary?.dau ?? 0)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard label="근무기록자 수" value={fmt(summary?.workRecorderCount ?? 0)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard label="전체 유저" value={fmt(summary?.totalUsers ?? 0)} />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 1 }}>최근 7일 일별 통계</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>날짜</TableCell>
              <TableCell align="right">발행 포인트</TableCell>
              <TableCell align="right">사용 포인트</TableCell>
              <TableCell align="right">DAU (활성 유저)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {daily?.length ? daily.map((d) => (
              <TableRow key={d.date}>
                <TableCell>{d.date}</TableCell>
                <TableCell align="right">{fmt(d.issued)}</TableCell>
                <TableCell align="right">{fmt(d.used)}</TableCell>
                <TableCell align="right">{fmt(d.dau)}</TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={4} align="center">데이터가 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>{year}년 월별 통계</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>월</TableCell>
              <TableCell align="right">발행 포인트</TableCell>
              <TableCell align="right">사용 포인트</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {monthly?.map((m) => (
              <TableRow key={m.month}>
                <TableCell>{m.month}</TableCell>
                <TableCell align="right">{fmt(m.issued)}</TableCell>
                <TableCell align="right">{fmt(m.used)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Dashboard;
